import { useState } from 'react';
import { FolderPlus, Loader2, Shapes } from 'lucide-react';
import { CARPETA_POR_TIPO } from '../../utils/brandAssets';

/**
 * Elementos de marca sin carpetas — ADR-2026-10-19-elementos-de-marca.
 *
 * Qué va acá y un toque para empezar con las tres carpetas de siempre (Logos, Elementos
 * gráficos, Tipografías: las mismas que usa el Estudio cuando sube sin carpeta), o una
 * con otro nombre.
 */
export const CARPETAS_SUGERIDAS = [CARPETA_POR_TIPO.logo!, CARPETA_POR_TIPO.element!, CARPETA_POR_TIPO.font!];

export const VacioDeElementos = ({
  onCrearSugeridas,
  onCrear,
}: {
  onCrearSugeridas: () => Promise<void>;
  onCrear: (nombre: string) => Promise<void>;
}) => {
  const [nombre, setNombre] = useState('');
  const [trabajando, setTrabajando] = useState<'sugeridas' | 'una' | null>(null);

  const correr = async (cual: 'sugeridas' | 'una', accion: () => Promise<void>) => {
    if (trabajando) return;
    setTrabajando(cual);
    try {
      await accion();
    } finally {
      setTrabajando(null);
    }
  };

  return (
    <div className="mx-auto flex max-w-[520px] flex-col items-center py-10 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary">
        <Shapes className="h-6 w-6" strokeWidth={1.75} />
      </span>
      <h3 className="text-[17px] font-semibold text-brand-black">Los elementos de tu marca, en un solo lugar</h3>
      <p className="mt-2 text-[13.5px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.7)]">
        Guardá acá tus logos, elementos gráficos y tipografías, ordenados en carpetas. Después los elegís al crear con
        el Asistente, y también aparecen en el Estudio de Video.
      </p>

      <button
        type="button"
        onClick={() => correr('sugeridas', onCrearSugeridas)}
        disabled={trabajando !== null}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 text-[14px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {trabajando === 'sugeridas' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
        Crear {CARPETAS_SUGERIDAS.map((c) => c.nombre).join(', ').replace(/, ([^,]*)$/, ' y $1')}
      </button>

      <form
        className="mt-4 flex w-full max-w-[360px] items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const limpio = nombre.trim();
          if (!limpio) return;
          void correr('una', () => onCrear(limpio));
        }}
      >
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={60}
          placeholder="O creá una con otro nombre"
          className="min-w-0 flex-1 rounded-full border border-black/10 bg-white px-4 py-2.5 text-[13.5px] text-brand-black outline-none focus:border-brand-primary"
        />
        <button
          type="submit"
          disabled={!nombre.trim() || trabajando !== null}
          className="shrink-0 rounded-full bg-neutral-100 px-4 py-2.5 text-[13.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.9)] transition-colors hover:bg-neutral-200 disabled:opacity-50"
        >
          {trabajando === 'una' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear'}
        </button>
      </form>
    </div>
  );
};

export default VacioDeElementos;
